<script setup lang="ts">
const cookieTypes = [
  'identifiers',
  'commercialInfo',
  'internetActivity',
  'geolocation',
  'sensoryData',
  'inferences',
  'sensitiveInfo',
]

const dataRetentionTypes = [
  'identifiers',
  'customerRecords',
  'commercialInfo',
  'internetActivity',
  'geolocation',
  'demographic',
  'sensoryData',
  'sensitiveInfo',
]

const infoTypes = {
  identifiers:
    'Identifiers (such as a name, address, unique personal identifier, email, phone number)',
  customerRecords: 'User records such as account and password Information',
  commercialInfo:
    'Commercial information such as records of products or services purchased, obtained, or considered',
  internetActivity: 'Internet/electronic activity',
  geolocation: 'Geolocation (outside of 1750 feet)',
  demographic: 'Demographic Information',
  sensoryData:
    'Sensory data, such as audio, electronic, visual (including support calls and video clips) or other similar information',
  inferences: 'Inferences about preferences, characteristics',
  sensitiveInfo:
    'Sensitive Information which consists of account login data and possible health data (such as pregnancy diagnosis) for users of the Service; and for job applicants/employees may include social security number, driver’s license number, racial/ethnic origins, religious or philosophical beliefs, contents of emails or texts to others, genetic/biometric data, sexual orientation data',
}
</script>

<template>
  <div class="PIITable">
    <div class="PIITable_Container">
      <table id="privacy-policy-chart" class="PIITable_Table">
        <thead class="PIITable_Head">
          <tr class="PIITable_Row">
            <th
              v-for="heading in [
                'Type of Information',
                'Sources of Information',
                'How We Use Information',
                'How We Share Information',
              ]"
              :key="heading"
              scope="col"
              class="PIITable_Heading"
            >
              {{ heading }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(infoType, key) in infoTypes"
            :key="key"
            class="PIITable_Row"
          >
            <th scope="row" class="PIITable_Heading">
              {{ infoType }}
            </th>
            <td class="PIITable_Data">
              <SkyLink block to="#info-we-collect" class="PIITable_Link">
                Type Of Information We Collect
              </SkyLink>
              <SkyLink
                v-if="cookieTypes.includes(key)"
                block
                to="/policies#cookie-policy"
                class="PIITable_Link"
              >
                Category of Use of Cookies
              </SkyLink>
            </td>
            <td class="PIITable_Data">
              <SkyLink block to="#how-info-used" class="PIITable_Link">
                How We Use The Information That We Collect
              </SkyLink>
              <SkyLink
                v-if="cookieTypes.includes(key)"
                block
                to="/policies#how-we-use-cookies"
                class="PIITable_Link"
              >
                Description
              </SkyLink>
              <SkyLink
                v-if="dataRetentionTypes.includes(key)"
                block
                to="#data-retention"
                class="PIITable_Link"
              >
                Data Retention
              </SkyLink>
            </td>
            <td class="PIITable_Data">
              <SkyLink block to="#disclosure-policy" class="PIITable_Link">
                When We Disclose The Information That We Collect
              </SkyLink>
              <SkyLink block to="#third-party-services" class="PIITable_Link">
                Links to Third Party Services
              </SkyLink>
              <SkyLink
                v-if="
                  [
                    'commercialInfo',
                    'internetActivity',
                    'geolocation',
                    'sensoryData',
                    'inferences',
                    'sensitiveInfo',
                  ].includes(key)
                "
                block
                to="/policies#third-party-cookies"
                class="PIITable_Link"
              >
                Third Party Cookies
              </SkyLink>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style lang="scss">
.PIITable {
  margin-bottom: var(--spacing-4x);

  &_Container {
    max-width: 90vw;
    overflow: auto;
  }

  &_Table {
    border: 1px solid var(--border-color-default);
    border-collapse: collapse;
    word-wrap: break-word;
  }

  &_Link {
    width: fit-content;
  }

  &_Head {
    background-color: var(--surface-color-cream);
  }

  &_Heading,
  &_Data {
    @include type-body('s', true);

    text-align: left;
    padding: var(--spacing-4x) var(--spacing-2x);
    width: 22%;
  }

  &_Heading {
    font-family: var(--font-family-default-medium);
    font-weight: var(--font-weight-medium);
  }

  &_Row:nth-child(even) {
    background-color: var(--color-frost);
  }
}
</style>
